import type { TimelineController } from "../shared/core/controllers/TimelineController";

function splitParagraph(paragraph: HTMLElement): HTMLElement[] {
	const walker = document.createTreeWalker(paragraph, NodeFilter.SHOW_TEXT);
	const textNodes: Text[] = [];
	while (walker.nextNode()) {
		if (walker.currentNode instanceof Text) {
			textNodes.push(walker.currentNode);
		}
	}
	const words: HTMLElement[] = [];
	for (const text of textNodes) {
		const fragment = document.createDocumentFragment();
		// Keep spaces and existing hyphen break opportunities, including inside links and emphasis.
		for (const token of text.data.match(/\s+|[^\s-]+-?|-+/g) ?? []) {
			if (/^\s+$/.test(token)) {
				fragment.append(token);
			} else {
				const word = document.createElement("span");
				word.className = "smash-word";
				word.textContent = token;
				fragment.append(word);
				words.push(word);
			}
		}
		text.replaceWith(fragment);
	}
	return words;
}

function collectPieces(root: Element): Element[] {
	const pieces: Element[] = [];
	for (const node of root.children) {
		if (
			node.matches("script, style, link, template, noscript, .hero, .skip-link")
		) {
			continue;
		}
		if (node instanceof HTMLElement && node.matches("p")) {
			pieces.push(...splitParagraph(node));
		} else if (
			node.localName.includes("-") ||
			node.matches(
				"h1, h2, h3, h4, h5, h6, li, form, img, picture, video, audio, canvas, svg, iframe, object, hr, table, button, input, select, textarea, .smash-divider",
			) ||
			[...node.childNodes].some(
				(child) => child instanceof Text && child.data.trim() !== "",
			)
		) {
			// Move a card/control as one piece, never both a parent and its descendants.
			pieces.push(node);
		} else {
			pieces.push(...collectPieces(node));
		}
	}
	return pieces;
}

export async function prepareSmashDebris(
	timeline: TimelineController,
	power: number,
): Promise<void> {
	const viewport = document.documentElement;
	const height = viewport.clientHeight;
	const width = viewport.clientWidth;
	const origin = { x: width / 2, y: viewport.scrollHeight + height / 2 };
	const scrollY = window.scrollY;
	await new Promise<void>((resolve) => {
		setTimeout(resolve, 0);
	});
	for (const section of document.querySelectorAll(
		".homepage > section + section",
	)) {
		const divider = document.createElement("span");
		divider.className = "smash-divider";
		divider.setAttribute("aria-hidden", "true");
		section.prepend(divider);
	}
	viewport.classList.add("smash-active");
	const pieces = collectPieces(document.body);
	// Finish every DOM write before measuring, then hand all trajectories to the compositor.
	const measured = pieces.map((node) => ({
		node,
		rect: node.getBoundingClientRect(),
	}));
	const gravity = height * 1.8;
	const impulse = Math.sqrt(power);
	let prepared = 0;
	await new Promise<void>((resolve) => {
		setTimeout(resolve, 0);
	});
	for (const { node, rect } of measured) {
		if (rect.width === 0 || rect.height === 0) {
			continue;
		}
		const x = rect.left + rect.width / 2;
		const y = rect.top + rect.height / 2 + scrollY;
		const vy = -height * (0.95 + Math.random() * 0.35) * impulse;
		const clearance = Math.hypot(rect.width, rect.height);
		const fallDistance = Math.max(0, height + clearance * 2 - rect.top);
		// Keep the entire upward/downward arc, retiring pieces only once they fall below the screen.
		const duration =
			(-vy + Math.sqrt(vy * vy + 2 * gravity * fallDistance)) / gravity;
		const vx =
			((-vy * (x - origin.x)) / Math.max(height / 2, origin.y - y)) * 2.5 +
			(Math.random() - 0.5) * width * 0.2 * impulse;
		// A spun piece fits inside its bounding circle. Check the vertical arc only
		// while that circle's horizontal path overlaps the viewport.
		const half = clearance / 2;
		const from =
			vx === 0
				? 0
				: Math.max(0, Math.min((-half - x) / vx, (width + half - x) / vx));
		const to =
			vx === 0
				? duration
				: Math.min(
						duration,
						Math.max((-half - x) / vx, (width + half - x) / vx),
					);
		const yAt = (t: number): number =>
			rect.top + rect.height / 2 + vy * t + (gravity * t * t) / 2;

		const apex = Math.max(from, Math.min(to, -vy / gravity));
		const entersViewport =
			(vx !== 0 || (x + half >= 0 && x - half <= width)) &&
			from <= to &&
			yAt(apex) - half <= height &&
			Math.max(yAt(from), yAt(to)) + half >= 0;
		if (!entersViewport) {
			if (node instanceof HTMLElement || node instanceof SVGElement) {
				node.style.visibility = "hidden";
			}
			continue;
		}
		const spin =
			(Math.random() - 0.5) *
			(node.classList.contains("smash-word") ? 8 : 3) *
			Math.sqrt(impulse);
		const keyframes = Array.from({ length: 13 }, (_, index) => {
			const t = (duration * index) / 12;
			return {
				transform: `translate3d(${vx * t}px, ${vy * t + (gravity * t * t) / 2}px, 0) rotate(${spin * t}rad)`,
			};
		});
		const delay =
			(Math.max(0, origin.y - y) / origin.y) * 100 + Math.random() * 40;
		const animation = new Animation(
			new KeyframeEffect(node, keyframes, {
				duration: duration * 1000,
				delay,
				fill: "both",
				easing: "linear",
			}),
			document.timeline,
		);
		timeline.registerAnimation(animation);
		void animation.finished.then(
			() => {
				// Drop finished compositor layers without bringing the debris back into view.
				if (node instanceof HTMLElement || node instanceof SVGElement) {
					node.style.visibility = "hidden";
				}
				animation.cancel();
			},
			() => {
				/* Navigation cancels the shared timeline. */
			},
		);
		// Keep setup responsive on slower devices; the shared timeline starts the whole blast together.
		if (++prepared % 24 === 0) {
			// eslint-disable-next-line eslint/no-await-in-loop -- Yield between batches, not after allocating every animation at once.
			await new Promise<void>((resolve) => {
				setTimeout(resolve, 0);
			});
		}
	}
}
